import {DateTimeFormatter} from '@js-joda/core';
import {Locale} from '@js-joda/locale_en-us';
import {CircularProgress} from '@mui/material';
import {LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import deepmerge from 'deepmerge';
import type {Dispatch, ReactNode} from 'react';
import {createContext, useContext, useEffect, useReducer, useState} from 'react';
import {toast, ToastContainer} from 'react-toastify';
import ContainerCenteredGrid from '@/components/ContainerCenteredGrid';
import type {ApiFetch} from '@/components/MSAL/useApiFetch';
import useApiFetch, {apiEndpoint} from '@/components/MSAL/useApiFetch';
import 'react-toastify/dist/ReactToastify.css';

export type FirstDayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;
export type RoundToNearest = 5 | 6 | 10 | 15;
export type DefaultTimeInput = 'range' | 'duration';
export type InitialRange = 'this-week' | 'yesterday' | 'today';

export type BookmarkLink = {
    url : string;
    title : string;
    openInNew ?: boolean;
    favorite ?: boolean;
};

export type BookmarkLinkGroup = {
    name : string;
    links : BookmarkLink[];
};

export type Preferences = {
    dashboard : {
        linkGroups : BookmarkLinkGroup[];
    };
    locale ?: {
        useAmPmTime : boolean;
        useIsoDate : boolean;
        firstDayOfWeek : FirstDayOfWeek;
    };
    timeEntry : {
        roundToNearest : RoundToNearest;
        defaultTimeInput : DefaultTimeInput;
        enterProjectAndComponent : boolean;
        initialRange : InitialRange;
        showProjectAndComponent : boolean;
        showTaskId : boolean;
        keepTimesOnDuplicate : boolean;
        alwaysUseFromDateForNewEntries : boolean;
    };
    scheduler : {
        favoriteEmployees : number[];
        selectedPracticeAreas : string[];
        selectedTeams : number[];
        onlyCstMembers : boolean;
    };
    capacity : {
        favoriteEmployees : number[];
        selectedPracticeAreas : string[];
        selectedTeams : number[];
        onlyCstMembers : boolean;
    };
};

export type PartialPreferences = {
    [P in keyof Preferences] ?: Partial<Preferences[P]>;
};

export type PreferencesReset = {
    [P in keyof Preferences] ?: Array<keyof Preferences[P]>;
};

const InitPreferences = 'INIT_PREFERENCES';
const UpdatePreferences = 'UPDATE_PREFERENCES';

type PreferencesState = {
    preferences : Preferences;
    i18n : I18n;
};

type InitPreferenceAction = {
    type : typeof InitPreferences;
    payload : {
        preferences : Preferences;
    };
};

type UpdatePreferenceAction = {
    type : typeof UpdatePreferences;
    payload : {
        update : PartialPreferences;
    };
};

type Actions = InitPreferenceAction | UpdatePreferenceAction;

const PreferencesStateContext = createContext<PreferencesState | null>(null);
const PreferencesDispatchContext = createContext<Dispatch<Actions> | null>(null);

const usePreferencesDispatch = () => {
    const context = useContext(PreferencesDispatchContext);

    if (!context) {
        throw new Error('Context was used before initial load');
    }

    return context;
};

const inferI18n = (preferences : Preferences) : {
    locale : Locale;
    inputDateFormat : string;
    timeFormatter : DateTimeFormatter;
} => {
    if (!preferences.locale) {
        return {
            locale: Locale.US,
            inputDateFormat: 'MM/dd/yyyy',
            timeFormatter: DateTimeFormatter.ofPattern('h:mm a').withLocale(Locale.US),
        };
    }

    const locale = Locale.US;

    // if (!locale.options) {
    //     locale.options = {};
    // }
    //
    // locale.options.weekStartsOn = preferences.locale.firstDayOfWeek;

    return {
        locale,
        inputDateFormat: preferences.locale.useIsoDate ? 'yyyy-MM-dd' : 'MM/dd/yyyy',
        timeFormatter: DateTimeFormatter.ofPattern(
            preferences.locale.useAmPmTime ? 'h:mm a' : 'HH:mm',
        ).withLocale(Locale.US),
    };
};

const preferencesReducer = (state : PreferencesState | null, action : Actions) : PreferencesState | null => {
    switch (action.type) {
        case InitPreferences:
            return {
                preferences: {
                    ...action.payload.preferences,
                },
                i18n: inferI18n(action.payload.preferences),
            };

        case UpdatePreferences: {
            if (!state) {
                throw new Error('Update called before initial load');
            }

            const preferences = deepmerge<Preferences, PartialPreferences>(
                state.preferences,
                action.payload.update,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                {arrayMerge: (_target : PartialPreferences[], source : PartialPreferences[]) => source}
            );

            return {
                preferences,
                i18n: inferI18n(preferences),
            };
        }

        default:
            return state;
    }
};

type Props = {
    children : ReactNode;
    categories : Array<keyof Preferences>;
};

type I18n = {
    locale : Locale;
    inputDateFormat : string;
    timeFormatter : DateTimeFormatter;
};

const PreferencesProvider : React.FC<Props> = ({children, categories} : Props) => {
    const [loading, setLoading] = useState(false);
    const [state, dispatch] = useReducer(preferencesReducer, null);
    const apiFetch = useApiFetch();

    useEffect(() => {
        if (loading || state) {
            return;
        }

        setLoading(true);

        void (async () => {
            const url = new URL('/v1/preferences', apiEndpoint);

            for (const category of categories) {
                url.searchParams.append('category', category);
            }

            try {
                const result = await apiFetch(url.href);

                if (!result.ok) {
                    toast('Could not load preferences.');
                    return;
                }

                const data = await result.json() as Preferences;

                dispatch({
                    type: InitPreferences,
                    payload: {preferences: data},
                });
            } catch (e) {
                toast('Could not load preferences.');
            }
        })();
    }, [dispatch, loading, state, categories, apiFetch]);

    if (!state) {
        return <ContainerCenteredGrid>
            <CircularProgress/>
        </ContainerCenteredGrid>;
    }

    return (
        <PreferencesStateContext.Provider value={state}>
            <PreferencesDispatchContext.Provider value={dispatch}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    {children}
                    <ToastContainer/>
                </LocalizationProvider>
            </PreferencesDispatchContext.Provider>
        </PreferencesStateContext.Provider>
    );
};

export const usePreferencesState = () : PreferencesState => {
    const context = useContext(PreferencesStateContext);

    if (!context) {
        throw new Error('Context was used before initial load');
    }

    return context;
};

let queue : PartialPreferences = {};
let updatePromise : Promise<unknown> | null = null;
let updateQueued = false;

type UpdatePreferences = (update : PartialPreferences) => Promise<void>;

const performUpdate = async (apiFetch : ApiFetch) => {
    const url = new URL('/v1/preferences', apiEndpoint);
    updatePromise = apiFetch(url.href, {
        method: 'PATCH',
        body: JSON.stringify(queue),
    });
    queue = {};

    try {
        await updatePromise;
    } catch (e) {
        toast('Could not update preferences.');
    }

    updatePromise = null;
};

export const useUpdatePreferences = () : UpdatePreferences => {
    const dispatch = usePreferencesDispatch();
    const apiFetch = useApiFetch();

    return async (update : PartialPreferences) => {
        dispatch({
            type: UpdatePreferences,
            payload: {update},
        });

        queue = deepmerge(
            queue,
            update,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            {arrayMerge: (_target : PartialPreferences[], source : PartialPreferences[]) => source}
        );

        if (updateQueued) {
            return;
        }

        if (!updatePromise) {
            return performUpdate(apiFetch);
        }

        updateQueued = true;
        await updatePromise;
        updateQueued = false;
        return performUpdate(apiFetch);
    };
};

type ResetPreferences = (reset : PreferencesReset) => Promise<void>;

export const useResetPreferences = () : ResetPreferences => {
    const apiFetch = useApiFetch();
    const dispatch = usePreferencesDispatch();

    return async (reset : PreferencesReset) => {
        if (updatePromise) {
            await updatePromise;
        }

        if (updateQueued) {
            // We resolved first, so we have to wait a moment for the next update.
            await new Promise(resolve => window.requestAnimationFrame(resolve));

            if (updatePromise) {
                await updatePromise;
            }
        }

        // At this point, no further update should be running.
        const url = new URL('/v1/preferences/reset', apiEndpoint);
        let response;

        try {
            response = await apiFetch(url.href, {
                method: 'PATCH',
                body: JSON.stringify(reset),
            });
        } catch (e) {
            toast('Could not reset preferences.');
            return;
        }

        const update = await response.json() as PartialPreferences;

        dispatch({
            type: UpdatePreferences,
            payload: {update},
        });
    };
};

export default PreferencesProvider;
